import * as React from 'react';
import {
    List,
    Datagrid,
    FunctionField,
    Confirm,
    useNotify,
    useRefresh,
} from 'react-admin';
import AddIcon from '@mui/icons-material/Add';
import { useLocation } from 'react-router-dom';
import { Box, Divider, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { IconButton } from '../icon-button/IconButton.tsx';
import { useParams } from 'react-router-dom';
import { EmptyBox } from "../empty-box/EmptyBox.tsx";
import { CategoryAddDialog } from '../dialogs/CategoryAddDialog.tsx';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CategoryService from '../../services/category/categoryService.ts';
import * as Sentry from '@sentry/react';

//TODO: move styles to a separate file if possible

const LocationCategoryTableActions = ({ open, setOpen }) => {
    const location = useLocation();
    const locationName = (location.state as { locationName?: string } | null)?.locationName;
    const { t } = useTranslation();

    return (
        <Box
            sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
            }}>
            <Box
                sx={{
                    paddingTop: 'var(--space-72)',
                    paddingBottom: 'var(--space-32)',
                    flex: 1,
                    display: 'flex',
                    alignItems: 'start',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                }}
            >
                <Typography variant="h5" sx={{
                    paddingLeft: 'var(--space-24)',
                    fontWeight: 700,
                    fontSize: 'var(--text-xxl)',
                }}>
                    {t('resources.categories')}
                </Typography>
                <Divider
                    variant="middle"
                    sx={{
                        width: '460px',
                        alignSelf: 'stretch',
                        my: 'var(--space-24)',
                        borderTopWidth: 1,
                        borderColor: 'var(--color-secondary)',
                    }}
                />

                <Typography variant="h5" sx={{
                    paddingLeft: 'var(--space-24)',
                    fontWeight: 700,
                    fontSize: 'var(--text-lg)',
                }}>
                    {locationName}
                </Typography>
            </Box>
            <Box
                sx={{
                    paddingTop: 'var(--space-72)',
                    paddingBottom: 'var(--space-32)',
                    width: '460px',
                    display: 'flex',
                    alignItems: 'end',
                    flexDirection: 'column',
                    justifyContent: 'center',
                }}
            >
                <IconButton icon={<AddIcon />} label={t('locationItems.category').toLocaleUpperCase()}
                    onClick={() => setOpen(true)} />
            </Box>
            <CategoryAddDialog open={open} onClose={() => setOpen(false)} />
        </Box>
    );
};

export const LocationCategoryTable = () => {
    const { t } = useTranslation();
    const { id } = useParams();
    const [confirmOpen, setConfirmOpen] = React.useState(false);
    const [toDelete, setToDelete] = React.useState<number | null>(null);
    const notify = useNotify();
    const refresh = useRefresh();
    const [open, setOpen] = React.useState(false);
    const categoryService = new CategoryService();

    return (
        <Box>
            <LocationCategoryTableActions open={open} setOpen={setOpen} />
            <List
                title={t("resources.categories")}
                actions={false}
                filter={{ locationId: id }}
                empty={
                    <EmptyBox
                        title="No items found"
                        description="There are no items yet."
                        onClick={() => {
                            setOpen(true);
                        }}
                    />
                }
                perPage={25}
                resource="categories"
                sx={{
                    '& .RaList-main': { px: 4 },
                    '& .RaList-content': {
                        border: '1px solid var(--color-secondary)',
                        borderRadius: 'var(--radius-6)',
                    },
                    '& .RaDatagrid-tbody tr:last-of-type td': {
                        borderBottom: 'none',
                    },
                    '& th.MuiTableCell-root': {
                        paddingLeft: 'var(--space-24)',
                        py: 'var(--space-16)',
                        fontSize: 'var(--text-xs)',
                    },
                    '& td.MuiTableCell-root': {
                        py: 'var(--space-24)',
                        paddingLeft: 'var(--space-24)',
                        fontSize: 'var(--text-xs)',
                    },
                    '& .MuiTablePagination-root': {
                        '& .MuiSelect-select': {
                            paddingRight: '32px !important',
                            paddingLeft: '12px !important',
                        },
                        '& .MuiSelect-icon': {
                            right: '8px',
                        }
                    },
                }}
            >
                <Datagrid
                    bulkActionButtons={false}
                    rowClick={false}
                    sx={{
                        '& .RaDatagrid-headerCell': {
                            fontWeight: 700,
                            letterSpacing: 0.2,
                        },
                    }}
                >
                    <FunctionField
                        sortable={false}
                        label={t('members.columns.id')}
                        render={(record: any) => (record?.id ?? '')}
                    />
                    <FunctionField
                        sortable={false}
                        label={t('members.columns.name')}
                        render={(record: any) => (record?.name ?? '')}
                    />
                    <FunctionField
                        label=""
                        sortable={false}
                        render={record => (
                            <>
                                <IconButton icon={<DeleteOutlineIcon />} color="var(--color-text-primary)" onClick={() => {
                                    setConfirmOpen(true);
                                    setToDelete(record.id);
                                }}
                                />
                            </>
                        )}
                    />
                </Datagrid>
            </List>
            <Confirm
                isOpen={confirmOpen}
                title={t('category.dialog.confirmDeletionTitle',)}
                content={t('category.dialog.confirmDeletionBody')}
                confirm={t('common.delete')}
                cancel={t('common.cancel')}
                onConfirm={async () => {
                    try {
                        await categoryService.deleteCategory(toDelete);
                        notify(t('common.saved'), { type: 'success' });
                        refresh();
                        setConfirmOpen(false);
                        setToDelete(null);
                    } catch (error) {
                        notify(error.response.data.error, { type: 'error' });
                        console.error(error.response.data.error);
                        Sentry.captureException(error);
                        setConfirmOpen(false);
                        setToDelete(null);
                    }
                }}
                onClose={() => {
                    setConfirmOpen(false);
                    setToDelete(null);
                }}
            />
        </Box>
    )
};
